import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Briefcase, 
  LineChart, 
  Newspaper, 
  Bell, 
  Settings,
  Menu,
  X,
  TrendingUp,
  Radar,
  Star,
  Calendar,
  FileText,
  FlaskConical,
  Brain,
  LogOut,
  User,
  Gem,
  Target,
  Rocket,
  Diamond,
  ClipboardList,
  Beaker,
  Tv,
  LineChart as LineChartIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationPanel } from "@/components/NotificationPanel";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Crown } from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/falcon-view", label: "Falcon View", icon: Tv, highlight: true },
  { path: "/portfolio", label: "Portfolio", icon: Briefcase },
  { path: "/sohel-pick", label: "Sohel Pick", icon: Star, highlight: true },
  { path: "/emergent-promise", label: "Emergent Promise", icon: Diamond, highlight: true },
  { path: "/sohel-analysis", label: "Sohel Analysis", icon: LineChartIcon, highlight: true },
  { path: "/paper-portfolio", label: "Paper Portfolio", icon: ClipboardList, highlight: true },
  { path: "/sohel-agentic-ai", label: "Sohel Agentic AI", icon: Brain, highlight: true },
  { path: "/experimental", label: "Experimental", icon: Beaker, highlight: true },
  { path: "/markets", label: "Markets", icon: LineChart },
  { path: "/sp-signals", label: "SP Do This", icon: Target },
  { path: "/explosive-movers", label: "Explosive", icon: Rocket },
  { path: "/smart-money", label: "Smart Money", icon: Crown },
  { path: "/future", label: "Future is Here", icon: Brain },
  { path: "/av-bot", label: "AV AI Bot", icon: Rocket },
  { path: "/scanner", label: "AI Scanner", icon: Radar },
  { path: "/hidden-gems", label: "Hidden Gems", icon: Gem },
  { path: "/earnings", label: "Earnings", icon: Calendar },
  { path: "/news", label: "Sohel News", icon: Newspaper },
  { path: "/alerts", label: "Alerts", icon: Bell },
  { path: "/settings", label: "Settings", icon: Settings },
];

const mobileNavItems = [
  { path: "/", icon: LayoutDashboard, label: "Home" },
  { path: "/sohel-pick", icon: Star, label: "My Picks" },
  { path: "/smart-money", icon: Crown, label: "Smart $" },
  { path: "/explosive-movers", icon: Rocket, label: "Explosive" },
  { path: "/sp-signals", icon: Target, label: "Signals" },
  { path: "/settings", icon: Settings, label: "More" },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get("/notifications?limit=20");
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-[#050505]">
      {/* Logo */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-white">SP WEALTH</h1>
            <p className="text-xs text-gray-500">Investment Radar</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-2 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive 
                    ? "bg-purple-500/20 text-purple-400 border-l-2 border-purple-500" 
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }
                `}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User Footer */}
      <div className="p-4 border-t border-gray-800">
        {user && (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start gap-2 text-gray-400 hover:text-white hover:bg-gray-800"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:border-r lg:border-gray-800">
        <SidebarContent />
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/80"
          onClick={() => setMobileMenuOpen(false)}
        >
          {/* Mobile Sidebar */}
          <div 
            className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-[#050505] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-gray-800">
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center gap-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 -ml-2 text-gray-400 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="font-bold text-base lg:text-lg text-white truncate">
                {navItems.find(item => item.path === location.pathname)?.label || "Dashboard"}
              </h1>
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-gray-400 hover:text-white relative"
                data-testid="notification-bell-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <NotificationPanel
                  notifications={notifications}
                  onClose={() => setShowNotifications(false)}
                  onRefresh={fetchNotifications}
                />
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6 overflow-x-hidden">
          {children}
        </main>

        {/* Mobile Bottom Navigation - Only visible on mobile */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-gray-800 z-50 safe-area-bottom">
          <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center justify-center py-2 px-3 min-w-[60px] transition-colors ${
                    isActive ? "text-purple-400" : "text-gray-500"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-purple-400' : ''}`} />
                  <span className="text-[10px] mt-1 font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};
